// WhatsApp Threat Center widget — 2026-05-16.
// Polls /api/whatsapp/threat-center and renders a live security overview.
// Two display modes: compact (small card for dashboards) and full
// (large panel with offenders + recent events table).

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Ban, EyeOff, UserCheck,
  Loader2, Phone, Clock, ChevronRight, RefreshCw, Activity,
} from "lucide-react";

export default function WhatsAppThreatWidget({ compact = false, autoRefreshSec = 60 }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API_URL}/whatsapp/threat-center`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch {
      // soft fail — widget hides itself if user lacks staff access
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (autoRefreshSec > 0) {
      const id = setInterval(fetchData, autoRefreshSec * 1000);
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border p-4 ${compact ? "min-h-[120px]" : "min-h-[200px]"} flex items-center justify-center`} data-testid="threat-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const s = data.stats || {};
  const total =
    (s.enumeration_attempts_24h || 0)
    + (s.rate_limited_24h || 0)
    + (s.failed_registrations_24h || 0)
    + (s.cross_company_attempts_24h || 0)
    + (s.ai_fallback_blocked_24h || 0);
  const isQuiet = total === 0 && (s.enumeration_lockouts_active || 0) === 0;

  if (compact) {
    return (
      <button
        onClick={() => navigate("/whatsapp/users")}
        className={`w-full rounded-xl border p-4 text-left active:opacity-80 transition-shadow hover:shadow-md ${
          isQuiet
            ? "bg-emerald-50 border-emerald-200"
            : total > 10
              ? "bg-red-50 border-red-200"
              : "bg-amber-50 border-amber-200"
        }`}
        data-testid="threat-widget-compact"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className={`text-xs font-semibold uppercase tracking-wide ${
              isQuiet ? "text-emerald-700" : total > 10 ? "text-red-700" : "text-amber-700"
            }`}>
              WhatsApp Threat Center
            </div>
            <div className={`text-2xl font-bold mt-1 ${
              isQuiet ? "text-emerald-700" : total > 10 ? "text-red-700" : "text-amber-700"
            }`}>
              {isQuiet ? "All quiet" : `${total} blocked`}
              {(s.enumeration_lockouts_active || 0) > 0 && (
                <span className="ml-2 text-xs font-medium">
                  ({s.enumeration_lockouts_active} locked out)
                </span>
              )}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Last 24h · {s.successful_registrations_24h || 0} new registrations
            </div>
          </div>
          {isQuiet ? (
            <ShieldCheck className="w-7 h-7 text-emerald-500" />
          ) : (
            <ShieldAlert className={`w-7 h-7 ${total > 10 ? "text-red-500" : "text-amber-500"}`} />
          )}
        </div>
      </button>
    );
  }

  // Full mode
  return (
    <div className="bg-white rounded-xl border p-4 space-y-4" data-testid="threat-widget-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isQuiet ? (
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-red-600" />
          )}
          <h2 className="font-semibold text-slate-800">
            🛡 Threat Center
            <span className="ml-2 text-xs font-normal text-slate-500">last {data.window_hours}h</span>
          </h2>
        </div>
        <button onClick={fetchData} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="threat-widget-refresh">
          <RefreshCw className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <ThreatTile icon={Ban} label="Lockouts active" value={s.enumeration_lockouts_active || 0} color="red" highlight={(s.enumeration_lockouts_active || 0) > 0} />
        <ThreatTile icon={EyeOff} label="Enumeration tries" value={s.enumeration_attempts_24h || 0} color="amber" />
        <ThreatTile icon={Clock} label="Rate-limited" value={s.rate_limited_24h || 0} color="amber" />
        <ThreatTile icon={AlertTriangle} label="Failed registrations" value={s.failed_registrations_24h || 0} color="amber" />
        <ThreatTile icon={ShieldAlert} label="Cross-company peeks" value={s.cross_company_attempts_24h || 0} color="red" highlight={(s.cross_company_attempts_24h || 0) > 0} />
        <ThreatTile icon={UserCheck} label="New registrations" value={s.successful_registrations_24h || 0} color="emerald" />
      </div>

      {/* Top offenders */}
      {(data.top_offenders || []).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
            Top offenders (last 24h)
          </h3>
          <div className="space-y-1.5">
            {data.top_offenders.slice(0, 5).map((o, i) => (
              <button
                key={o.phone}
                onClick={() => navigate(`/whatsapp/users?search=${encodeURIComponent(o.phone)}`)}
                className="w-full flex items-center justify-between p-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-left text-sm group"
                data-testid={`offender-${i}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="font-mono text-slate-700">{o.phone}</span>
                  <span className="text-xs text-slate-400 truncate hidden sm:inline">
                    {(o.events || []).slice(0, 3).join(", ")}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                    {o.count}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent events */}
      {(data.recent_events || []).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
            Recent high-severity events
          </h3>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.recent_events.map((e, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 rounded text-xs">
                <Activity className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-slate-800">{e.event_type}</span>
                    <span className="text-slate-500">{e.phone_number || "(unknown)"}</span>
                  </div>
                  {e.detail && <div className="text-slate-600 truncate">{e.detail}</div>}
                </div>
                <span className="text-slate-400 flex-shrink-0">
                  {e.created_at && new Date(e.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isQuiet && (data.top_offenders || []).length === 0 && (data.recent_events || []).length === 0 && (
        <div className="text-center py-6 text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm font-medium">All quiet on the WhatsApp front</p>
          <p className="text-xs text-emerald-600 mt-1">No blocks, lockouts or suspicious activity in the last 24h.</p>
        </div>
      )}
    </div>
  );
}

function ThreatTile({ icon: Icon, label, value, color, highlight = false }) {
  const colors = {
    red: highlight ? "bg-red-100 border-red-300 text-red-700" : "bg-slate-50 border-slate-200 text-slate-700",
    amber: highlight ? "bg-amber-100 border-amber-300 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`border rounded-lg p-2 ${colors[color] || colors.amber}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</span>
      </div>
      <div className={`text-xl font-bold mt-1 ${value > 0 ? "" : "opacity-40"}`}>{value}</div>
    </div>
  );
}
